import { useEffect } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { SUCCESS_REDIRECT_DELAY_MS } from "@/config/navigation";
import {
  signUpAsync,
  confirmSignUpAsync,
  resendCodeAsync,
  resetSignupState,
  clearSignupError,
  decrementCooldown,
  selectSignup,
} from "@/store/slices/signupSlice";
import type { SignupFormData } from "@/schemas/signupSchemas";

/**
 * Signup State (from Redux)
 */
export interface SignupViewModelState {
  isSubmitting: boolean;
  isVerifying: boolean;
  error: string | null;
  email: string | null;
  resendCooldown: number;
}

/**
 * Signup Actions (user intents)
 */
export interface SignupViewModelActions {
  signUp: (formData: SignupFormData) => Promise<void>;
  verify: (code: string) => Promise<void>;
  resendCode: () => Promise<void>;
  clearError: () => void;
  navigateToLogin: () => void;
}

/**
 * Signup Computed Properties
 */
export interface SignupViewModelComputed {
  canSubmit: boolean;
  showVerification: boolean;
  canResend: boolean;
  hasError: boolean;
  errorMessage: string;
}

/**
 * Signup ViewModel Return Type
 */
export interface SignupViewModel {
  state: SignupViewModelState;
  actions: SignupViewModelActions;
  computed: SignupViewModelComputed;
  onMount: () => (() => void) | void;
}

/**
 * useSignupViewModel Hook
 *
 * MVVM ViewModel for user signup and email verification.
 * Structure: { state, actions, computed, onMount }
 *
 * @returns ViewModel with all signup logic
 */
export function useSignupViewModel(): SignupViewModel {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation("auth");
  const signup = useAppSelector(selectSignup);

  // State (mapped from Redux)
  const state: SignupViewModelState = {
    isSubmitting: signup.status === "submitting",
    isVerifying: signup.isConfirming, // Only true when submitting verification code
    error: signup.error,
    email: signup.email,
    resendCooldown: signup.resendCooldown,
  };

  // Actions (all user intents)
  // Note: React Compiler automatically memoizes these functions
  const actions: SignupViewModelActions = {
    signUp: async (formData: SignupFormData) => {
      try {
        await dispatch(signUpAsync(formData)).unwrap();
        // On success, status changes to 'verifying' automatically
        // Navigation to /verify happens in the page component
      } catch {
        // Error is already in Redux state
      }
    },

    verify: async (code: string) => {
      if (!state.email) {
        return;
      }

      try {
        await dispatch(confirmSignUpAsync({ email: state.email, code })).unwrap();

        // Show success toast
        toast.success(t("signup.success.emailVerified"));

        // Delay navigation to allow user to see the toast
        setTimeout(() => {
          navigate("/auth/login");
        }, SUCCESS_REDIRECT_DELAY_MS);
      } catch {
        // Error is already in Redux state
      }
    },

    resendCode: async () => {
      if (!state.email || state.resendCooldown > 0) {
        return;
      }

      try {
        await dispatch(resendCodeAsync(state.email)).unwrap();
      } catch {
        // Error is already in Redux state
      }
    },

    clearError: () => {
      dispatch(clearSignupError());
    },

    navigateToLogin: () => {
      navigate("/auth/login");
    },
  };

  // Computed properties
  const computed: SignupViewModelComputed = {
    canSubmit: !state.isSubmitting && !state.isVerifying,
    showVerification: signup.status === "verifying",
    canResend: state.resendCooldown === 0 && !state.isSubmitting,
    hasError: state.error !== null,
    errorMessage: state.error ? t(state.error) : "",
  };

  // Lifecycle
  // Note: React Compiler automatically memoizes this function
  const onMount = () => {
    // Reset signup state on mount
    dispatch(resetSignupState());

    // Cleanup function
    return () => {
      dispatch(clearSignupError());
    };
  };

  // Cooldown timer effect (separate from onMount)
  useEffect(() => {
    if (state.resendCooldown > 0) {
      const timer = setInterval(() => {
        dispatch(decrementCooldown());
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [state.resendCooldown, dispatch]);

  return { state, actions, computed, onMount };
}
